// Table of nightly export runs (one row per institution per day) with the
// per-run actions: see contents, download the Excel zip, re-copy to Drive.
import React from 'react';
import { CheckCircle, Clock, Download, Eye, Cloud, PlayCircle, XCircle } from 'lucide-react';
import type { TenantExportRun, TenantExportStatus } from '@/types/tenant-export.types';

export const formatBytes = (bytes: number | null): string => {
  if (!bytes) return '—';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const StatusBadge: React.FC<{ status: TenantExportStatus }> = ({ status }) => {
  const config: Record<TenantExportStatus, { label: string; color: string; icon: typeof Clock }> = {
    PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    RUNNING: { label: 'En cours', color: 'bg-blue-100 text-blue-800 animate-pulse', icon: PlayCircle },
    COMPLETED: { label: 'Terminé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    FAILED: { label: 'Échec', color: 'bg-red-100 text-red-800', icon: XCircle },
    CANCELLED: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  };
  const { label, color, icon: Icon } = config[status] ?? config.PENDING;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon size={14} />
      {label}
    </span>
  );
};

interface TenantExportRunsTableProps {
  runs: TenantExportRun[];
  loading: boolean;
  driveConfigured: boolean;
  busyRunId: string | null;
  onViewManifest: (run: TenantExportRun) => void;
  onDownload: (run: TenantExportRun) => void;
  onDriveSync: (run: TenantExportRun) => void;
}

export const TenantExportRunsTable: React.FC<TenantExportRunsTableProps> = ({
  runs, loading, driveConfigured, busyRunId, onViewManifest, onDownload, onDriveSync,
}) => {
  if (!loading && runs.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
        Aucun export pour l'instant. Le premier passage automatique a lieu chaque nuit à 04:00 UTC ;
        vous pouvez aussi lancer un export maintenant.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Institution</th>
              <th className="px-4 py-3">Lancé le</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Lignes</th>
              <th className="px-4 py-3 text-right">Documents</th>
              <th className="px-4 py-3">Drive</th>
              <th className="px-4 py-3 text-right">Excel</th>
              <th className="px-4 py-3 text-right">Durée</th>
              <th className="px-4 py-3">Par</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {runs.map((run) => {
              const done = run.status === 'COMPLETED';
              const busy = busyRunId === run.id;
              return (
                <tr key={run.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{run.tenantName}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(run.startedAt ?? run.createdAt)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={run.status} />
                    {run.errorMessage && (
                      <p className="mt-1 text-xs text-red-600 max-w-xs truncate" title={run.errorMessage}>{run.errorMessage}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{run.totalRecords?.toLocaleString('fr-FR') ?? '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                    {run.documents ? (
                      <span title={`+${run.documents.copied} copiés, ${run.documents.unchanged} déjà à jour`}>
                        {run.documents.listed.toLocaleString('fr-FR')}
                        {run.documents.missing > 0 && <span className="ml-1 text-xs text-amber-700">({run.documents.missing} introuvables)</span>}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {run.drive
                      ? run.drive.error
                        ? <span className="text-red-600" title={run.drive.error}>Échec</span>
                        : <span className="text-emerald-700" title={`+${run.drive.documentsCopied} documents`}>{formatDateTime(run.drive.syncedAt)}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{formatBytes(run.fileSize)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{formatDuration(run.durationSeconds)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{run.triggeredByEmail === 'system@nexedu.ma' ? 'Automatique' : run.triggeredByEmail}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => onViewManifest(run)}
                        disabled={!done}
                        title="Voir le contenu"
                        className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => onDownload(run)}
                        disabled={!done || busy}
                        title="Télécharger les classeurs Excel (zip)"
                        className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 disabled:opacity-30"
                      >
                        <Download size={16} />
                      </button>
                      {driveConfigured && (
                        <button
                          onClick={() => onDriveSync(run)}
                          disabled={!done || busy}
                          title="Recopier vers Google Drive"
                          className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-30"
                        >
                          <Cloud size={16} className={busy ? 'animate-pulse' : ''} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
